// CB:BEGIN fr-001-notifications-tab
import React, { useState } from 'react';
import { useCoreBidsSettings } from '../../hooks/useCoreBidsSettings';
import { SettingsSection } from '../settings/SettingsSection';
import { SettingsCheckbox } from '../settings/SettingsCheckbox';
import { SettingsSaveBar } from '../settings/SettingsSaveBar';

const EVENT_KEYS = [
  { key: 'bid_received', label: 'Bid Received' },
  { key: 'bid_submitted', label: 'Bid Submitted' },
  { key: 'invite_created', label: 'Invite Created' },
  { key: 'invite_opened', label: 'Invite Opened' },
  { key: 'project_message', label: 'Project Message' },
  { key: 'payout_released', label: 'Payout Released' },
  { key: 'admin_permission_changed', label: 'Admin Permission Changed' },
  { key: 'security_alert', label: 'Security Alert' },
];

export default function CoreBidsNotificationsTab() {
  const s = useCoreBidsSettings();
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Stop Clerk modal from closing on clicks (bubble phase only, on tab root)
  const stopModalBubble = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  if (s.loading) {
    return (
      <div onClick={stopModalBubble} style={{ padding: '24px', maxWidth: '600px' }}>
        <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>Loading...</div>
      </div>
    );
  }

  if (!s.data) {
    return (
      <div onClick={stopModalBubble} style={{ padding: '24px', maxWidth: '600px' }}>
        <div style={{ padding: '20px', color: '#dc2626' }}>
          {s.error || 'Failed to load settings'}
        </div>
      </div>
    );
  }

  const prefs = s.data.notificationPrefs;

  const handleChannelToggle = (channel: 'emailEnabled' | 'smsEnabled' | 'inAppEnabled', checked: boolean) => {
    s.updateLocal({
      notificationPrefs: {
        ...prefs,
        [channel]: checked,
      },
    });
  };

  const updatePerEvent = (eventKey: string, channel: string, enabled: boolean) => {
    const perEvent = prefs.perEvent || {};
    const eventPref = perEvent[eventKey] || {};
    s.updateLocal({
      notificationPrefs: {
        ...prefs,
        perEvent: {
          ...perEvent,
          [eventKey]: {
            ...eventPref,
            [channel]: enabled,
          },
        },
      },
    });
  };

  const handleSave = async () => {
    try {
      await s.save();
      // Show success message briefly
      setTimeout(() => {
        // Error state will be cleared by hook on successful save
      }, 3000);
    } catch (err) {
      // Error is already set in hook
    }
  };

  return (
    <div onClick={stopModalBubble} className="corebids-settings" style={{ padding: '24px', maxWidth: '600px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '24px', color: '#0f172a' }}>
        Notification Preferences
      </h2>

      {s.error && !s.error.startsWith('✅') && (
        <div style={{ 
          padding: '12px 16px', 
          background: '#fef2f2', 
          color: '#dc2626', 
          borderRadius: '8px', 
          marginBottom: '20px',
          fontSize: '14px',
          border: '1px solid #fecaca'
        }}>
          {s.error}
        </div>
      )}

      <SettingsSection title="Notification Channels">
        <div className="mb-5">
          <SettingsCheckbox
            id="email-notifications"
            label="Email notifications"
            checked={prefs.emailEnabled}
            onCheckedChange={(checked) => handleChannelToggle('emailEnabled', checked)}
            testId="notif-email"
          />
        </div>
        <div className="mb-5">
          <SettingsCheckbox
            id="sms-notifications"
            label="SMS notifications"
            checked={prefs.smsEnabled}
            onCheckedChange={(checked) => handleChannelToggle('smsEnabled', checked)}
            testId="notif-sms"
          />
        </div>
        <div className="mb-5">
          <SettingsCheckbox
            id="inapp-notifications"
            label="In-app notifications"
            checked={prefs.inAppEnabled}
            onCheckedChange={(checked) => handleChannelToggle('inAppEnabled', checked)}
            testId="notif-inapp"
          />
        </div>
      </SettingsSection>

      <SettingsSection title="Advanced Settings">
        <button
          type="button"
          onClick={() => setShowAdvanced(prev => !prev)}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#2563eb',
            cursor: 'pointer',
            textDecoration: 'none',
            padding: 0,
            marginBottom: '16px',
            fontSize: '14px',
            fontWeight: '500',
            userSelect: 'none'
          }}
          onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
          onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
        >
          {showAdvanced ? 'Hide' : 'Show'} Advanced Settings
        </button>

        {showAdvanced && (
          <div>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
              Override channel preferences for specific events
            </p>
            
            {EVENT_KEYS.map((event) => {
              const eventPref = prefs.perEvent?.[event.key] || {};
              return (
                <div 
                  key={event.key} 
                  className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="font-medium mb-3 text-sm text-gray-900">{event.label}</div>
                  <div className="mb-2">
                    <SettingsCheckbox
                      id={`${event.key}-email`}
                      label="Email"
                      checked={eventPref.email !== false && prefs.emailEnabled}
                      onCheckedChange={(checked) => updatePerEvent(event.key, 'email', checked)}
                      className="text-xs"
                    />
                  </div>
                  <div className="mb-2">
                    <SettingsCheckbox
                      id={`${event.key}-sms`}
                      label="SMS"
                      checked={eventPref.sms === true && prefs.smsEnabled}
                      onCheckedChange={(checked) => updatePerEvent(event.key, 'sms', checked)}
                      className="text-xs"
                    />
                  </div>
                  <div>
                    <SettingsCheckbox
                      id={`${event.key}-inapp`}
                      label="In-app"
                      checked={eventPref.inApp !== false && prefs.inAppEnabled}
                      onCheckedChange={(checked) => updatePerEvent(event.key, 'inApp', checked)}
                      className="text-xs"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SettingsSection>

      <SettingsSaveBar
        dirty={s.dirty}
        saving={s.saving}
        error={s.error}
        lastSavedAt={s.lastSavedAt}
        onSave={handleSave}
        onRevert={s.resetLocalToServer}
      />
    </div>
  );
}
// CB:END fr-001-notifications-tab
